import { ATTRIBUTION, LOCAL_IDEOGRAPH_FONTS, type MapMode, styleFor } from '@pf/map-style';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?url';
import { FetchSource, FileSource, PMTiles, Protocol } from 'pmtiles';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { isDark, onAppearance } from '../app/theme.ts';
import t from '../kit/type.module.css';
import { cachedArchive, localArchive } from './archives.ts';
import s from './FieldMap.module.css';

// One pmtiles:// protocol for the whole app. Archives are added as local files (archives.ts), so
// tiles are read from the browser's copy, not fetched.
const protocol = new Protocol();
let registered = false;
function registerPmtiles() {
  if (registered) return;
  // MapLibre 6 ships its worker as a separate module; let Vite hand out its URL.
  maplibregl.setWorkerUrl(workerUrl);
  maplibregl.addProtocol('pmtiles', protocol.tile);
  registered = true;
}

const contrast = matchMedia('(prefers-contrast: more)');
const subscribe = (onChange: () => void) => {
  contrast.addEventListener('change', onChange);
  const off = onAppearance(onChange);
  return () => {
    contrast.removeEventListener('change', onChange);
    off();
  };
};
/** The map follows the same system modes as the tokens. */
function useMapMode(): MapMode {
  return useSyncExternalStore(subscribe, () => {
    // The page's theme (Auto follows the system), and the system's contrast.
    return `${isDark() ? 'dark' : 'light'}${contrast.matches ? 'HighContrast' : ''}` as MapMode;
  });
}

/** A pin (patterns/maps.md §Our overlays): numbered day square, hollow list place, or hotel. */
export interface MapPin {
  id: string;
  lat: number;
  lng: number;
  label: string;
  /** CSS colour for day pins (a `--color-day-n` variable). */
  color?: string;
  kind: 'day' | 'hollow' | 'lodging';
  title: string;
}

export interface MapRoute {
  coords: [number, number][];
  color: string;
  dimmed?: boolean;
}

/**
 * The Field Survey basemap (patterns/maps.md) from a `.pmtiles` archive: a trip's offline extract
 * on R2, or a local file; with our pins and routes on top (F-02).
 */
export function FieldMap({
  tiles,
  bounds,
  label,
  pins = [],
  routes = [],
  selected,
  onSelect,
}: {
  /** URL of the `.pmtiles` archive. */
  tiles: string;
  bounds: [[number, number], [number, number]];
  /** What the map shows, for screen readers ("Map of Kyoto"). */
  label: string;
  pins?: MapPin[];
  routes?: MapRoute[];
  selected?: string | null;
  onSelect?: (id: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markers = useRef<maplibregl.Marker[]>([]);
  const mode = useMapMode();

  useEffect(() => {
    const element = container.current;
    if (!element) return;
    registerPmtiles();
    const url = new URL(tiles, location.href).href;
    let map: maplibregl.Map | undefined;
    let cancelled = false;
    setFailed(false);
    // From the browser cache when the archive is there; else straight away by range requests,
    // while the whole archive downloads into the cache for next time (a 98 MB Tokyo used to
    // leave the map blank until it had all arrived).
    cachedArchive(url)
      .then(async (file) => {
        if (cancelled) return;
        const archive = new PMTiles(file ? new FileSource(file) : new FetchSource(url)); // keyed by the URL
        // Only an archive that can't be opened is a failed map; a tile or a glyph going astray isn't.
        await archive.getHeader();
        if (cancelled) return;
        protocol.add(archive);
        // Fills the cache (or checks the cached copy is current, one HEAD) for next time.
        localArchive(url).catch(() => undefined);
        map = new maplibregl.Map({
          container: element,
          style: styleFor(mode, `pmtiles://${url}`),
          bounds,
          attributionControl: false,
          localIdeographFontFamily: LOCAL_IDEOGRAPH_FONTS,
          fadeDuration: 0,
        });
        mapRef.current = map;
        map.on('load', () => setReady((n) => n + 1));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      map?.remove();
      mapRef.current = null;
    };
  }, [tiles, bounds, mode]);

  // Routes: one GeoJSON source, a surface halo and the day-coloured line (4pt).
  const [ready, setReady] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `ready` re-runs this once the style has loaded
  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    const css = getComputedStyle(document.documentElement);
    const resolve = (c: string) =>
      c.startsWith('var(') ? css.getPropertyValue(c.slice(4, -1)).trim() || 'gray' : c;
    const data = {
      type: 'FeatureCollection' as const,
      features: routes.map((r) => ({
        type: 'Feature' as const,
        properties: { color: resolve(r.color), opacity: r.dimmed ? 0.35 : 1 },
        geometry: { type: 'LineString' as const, coordinates: r.coords },
      })),
    };
    const source = map.getSource('pf-routes') as maplibregl.GeoJSONSource | undefined;
    if (source) source.setData(data);
    else {
      map.addSource('pf-routes', { type: 'geojson', data });
      map.addLayer({
        id: 'pf-route-halo',
        type: 'line',
        source: 'pf-routes',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': resolve('var(--color-surface-base)'),
          'line-width': 6,
          'line-opacity': ['get', 'opacity'],
        },
      });
      map.addLayer({
        id: 'pf-route',
        type: 'line',
        source: 'pf-routes',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': 4, 'line-opacity': ['get', 'opacity'] },
      });
    }
  }, [routes, ready]);

  // Pins as HTML markers, so they use the type tokens (stop IDs in Space Grotesk).
  // biome-ignore lint/correctness/useExhaustiveDependencies: `ready` re-runs this once the map exists
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    for (const m of markers.current) m.remove();
    markers.current = pins.map((p) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = [s.pin, s[p.kind], p.id === selected ? s.selected : ''].join(' ');
      if (p.kind === 'day' && p.color && p.id !== selected) el.style.background = p.color;
      el.textContent = p.kind === 'lodging' ? '' : p.label;
      el.setAttribute('aria-label', p.title);
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onSelect?.(p.id);
      });
      return new maplibregl.Marker({ element: el }).setLngLat([p.lng, p.lat]).addTo(map);
    });
  }, [pins, selected, onSelect, ready]);

  return (
    <figure className={s.frame} aria-label={label}>
      <div ref={container} className={s.map} />
      {failed && (
        <p className={`${t.bodyS} ${s.failed}`} role="status">
          The map couldn't load. Check the connection; the places are still listed.
        </p>
      )}
      <figcaption className={`${t.micro} ${s.attribution}`}>{ATTRIBUTION}</figcaption>
    </figure>
  );
}
